import classNames from 'classnames';
import Spinner from 'components/Spinner';
import Button from 'components/Button';
import Ticket from './components/Ticket';
import useDeliveryPlanner from './useDeliveryPlanner';
import stat_time from './icons/stat_time.svg';

import styles from './DeliveryPlanner.module.scss';
import './styles.scss';
import nyPostalCodeBoroughOptions from 'shared/ny-postal-code-borough-options';
import {groupBy, isArray, map, sortBy} from 'lodash';
import {parseJSON} from 'date-fns';
import {
  getOrderDeliveryBorough,
  getOrderDeliveryTimePreference,
} from 'utils/order-helpers';

import SendRouteModal from 'components/SendRouteModal';
import FormGroup from 'components/FormGroup';
import Modal from 'components/Modal';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import CheckboxField from 'components/CheckboxField/CheckboxField';
import TextField from 'components/TextField';
import {components} from 'react-select';
import Select from 'components/Select';
import PermissionModal from 'components/PermissionModal';
import locationIcon from '../Requests/location.svg';
import LocationIcon from './icons/location';
import calendar from './icons/calendar.svg';
import DateTimePicker from 'components/DateTimePicker';
import Typography from 'components/Typography';
import FormActionGroup from 'components/FormActionGroup';
import FilterIcon from 'pages/Requests/FilterIcon';
import Clear from './icons/clear';
import Morning from './icons/morning';
import Afternoon from './icons/afternoon';
import Evening from './icons/evening';
import {Resizable} from 'react-resizable';
import 'react-resizable/css/styles.css';
import {GooglePlaceAutoComplete} from 'components/GooglePlaceAutoComplete';
import {DataTableSchemaItem, Order} from 'types';
import WorkerSelect from './components/WorkerSelect';
import useCompanyConfig from 'hooks/useCompanyConfig';
import OrderDetailsModal from './components/OrderDetailsModal';
import ButtonWithIcon from 'components/ButtonWithIcon';
import MapIcon from './icons/map';
import HistoryIcon from './icons/history';
import WorkerAssignmentBox from './components/WorkerAssignmentBox';
import NewMap from './components/NewMap';
import {Trash2Icon} from './icons/trash-2';
import MoveIcon from './icons/move';
import RotateIcon from './icons/rotate';
import PriorityFlag from 'components/PriorityFlag';
import SendIcon from './icons/send';
import MagicIcon from './icons/magic';

const locationOptionsGrouped = groupBy(nyPostalCodeBoroughOptions, 'label');
const locationOptions = sortBy(
  map(locationOptionsGrouped, (obj, key) => ({
    label: key,
    value: map(obj, 'value'),
  })),
  'label'
);

export const SUB_SCHEMA: DataTableSchemaItem[] = [
  {
    dataKey: 'full_name',
    header: 'Full Name',
    colWidth: '300px',
  },

  {
    dataKey: 'phone',
    header: 'Phone Number',
    colWidth: '250px',
  },
  {
    dataKey: 'field_role',
    header: 'Role',
  },

  {dataKey: 'view', header: ''},
];

export function getTemplateColumns(
  schema: DataTableSchemaItem[],
  selectable: boolean
) {
  const cols = [
    selectable ? 'minmax(32px, auto)' : undefined,
    ...schema.map((s) => s.colWidth || '1fr'),
  ]
    .filter((col) => Boolean(col))
    .join(' ');

  return cols;
}

function DeliveryPlanner() {
  const {
    filtered,
    route,
    teams,
    workers,
    isLoading,
    unassignedOrders,
    selectedUnassignedOrders,
    isSubmittable,
    onChangeUnassignedCheckbox,
    onChangeRouteParams,
    onChangeStartTime,
    onSendRoute,
    sortBy,
    setSortBy,
    filter,
    handleChangeLocation,
    assignSelected,
    moveToOrders,
    onChangeData,
    onChangePriority,
    isFetching,
    roundedTime,
    searchKey,
    setSearch,
    count,

    sortOptions,
    search,
    permissionRef,
    noteModal,

    calculateType,
    handleDragEnd,
    allChecked,
    calculateHandler,
    onMappingPressed,
    onChangePreferenceTime,
    onChangeCheckbox,
    isSendRouteModalOpen,
    setIsSendRouteModalOpen,
    moveOrderModal,
    setMoveToOrder,
    lastElementRef,
    resetFilter,
    applyFilter,
    showFilterModal,
    setShowFilterModal,
    mapSize,
    setMapSize,
    onDeleteClick,
    selectedWorker,
    onChangeWorker,
    currentTeam,
    resetCalculate,
    isAnimating,
    handleNoteClick,
    activeOrder,
    shouldRecalculateForEstimatedTime,
    handleNoteModalClose,
    refetchOrders,
    refetch,
    invalidOrderIds,
  } = useDeliveryPlanner();
  const isIndividual = !!selectedWorker && 'driver_id' in selectedWorker;
  const showSendButton =
    !!route?.deliveries?.length &&
    isSubmittable &&
    route.id !== '0' &&
    !!search;
  // invalidOrderIds.length === 0;

  const {labels} = useCompanyConfig();
  return (
    <>
      <div className={styles.root}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className={styles.panel}>
            <div className={styles.panel__header}>
              {filtered && <p>Filter:</p>}
              <div className={styles.row}>
                <span className={styles.panel__inbox_title}>
                  Inbox: {Math.max(count, 0)} {labels.deliveries}
                </span>
                {filtered && (
                  <div className={styles.clear_icon} onClick={resetFilter}>
                    <Clear />
                  </div>
                )}
              </div>
            </div>

            <div className={styles.search_wrapper}>
              <CheckboxField
                name={`checkbox`}
                className={styles.checkboxAll}
                checked={allChecked}
                onChange={onChangeCheckbox}
              />
              <TextField
                value={searchKey}
                onChange={(target) => setSearch(target.target.value)}
                wrapperClass={styles.searchWrapper}
                placeholder="Search..."
              />
              <div
                className={classNames(styles.filterBtn, {
                  [styles.filterBtnActive]: filtered,
                })}
                onClick={() => setShowFilterModal(true)}
              >
                <FilterIcon isFilled={filtered} />
              </div>
            </div>
            <div className={classNames(styles.panel__content, 'draggable-1')}>
              {isLoading ? (
                <div className={styles.spinner_loading}>
                  <Spinner />
                </div>
              ) : (
                <>
                  <Droppable droppableId="unassigned_list">
                    {(provided: any) => (
                      <div
                        className={styles.draggableContent}
                        ref={provided.innerRef}
                      >
                        {!!filtered && unassignedOrders.length < 1 && (
                          <div className={styles.no_result}>
                            <p>No deliveries with that filter</p>
                            <Button
                              onClick={resetFilter}
                              className={styles.no_result_button}
                            >
                              Reset filter
                            </Button>
                          </div>
                        )}
                        {unassignedOrders.length === 0 && (
                          <div className={styles.emptyDraggableContent}>
                            Drop here to unassign
                          </div>
                        )}
                        {unassignedOrders.map((ticket, index) => {
                          return (
                            <div
                              key={ticket.id}
                              ref={
                                unassignedOrders.length === index + 1
                                  ? lastElementRef
                                  : null
                              }
                            >
                              <Draggable
                                draggableId={String(ticket.id)}
                                index={index}
                              >
                                {(provided: any, snapshot: any) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <Ticket
                                      isDragging={snapshot.isDragging}
                                      key={ticket.id}
                                      onDeleteClick={onDeleteClick}
                                      {...ticket}
                                      index={index + 1}
                                      new_leaf_order={ticket.new_leaf_order}
                                      timePreference={getOrderDeliveryTimePreference(
                                        ticket
                                      )}
                                      borough={getOrderDeliveryBorough(ticket)}
                                      selected={selectedUnassignedOrders.includes(
                                        ticket.id
                                      )}
                                      onChangeCheckbox={
                                        onChangeUnassignedCheckbox
                                      }
                                      handleNoteClick={handleNoteClick(ticket)}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            </div>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                  {isFetching && unassignedOrders.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Spinner />
                    </div>
                  )}
                </>
              )}
            </div>
            {!!selectedUnassignedOrders.length && (
              <div className={styles.panel__bottom}>
                <Typography variant="label">
                  {selectedUnassignedOrders.length} orders selected
                </Typography>
                <div className={styles.panel__bottom__selected}>
                  <ButtonWithIcon
                    icon={<Trash2Icon width={16} height={16} />}
                    size="md"
                    className={styles.panel__remove}
                    color="outlined"
                    disabled={!selectedUnassignedOrders.length}
                    onClick={() => setMoveToOrder(true)}
                  >
                    Remove
                  </ButtonWithIcon>
                  <ButtonWithIcon
                    size="md"
                    icon={<MoveIcon width={16} height={16} />}
                    color="outlined"
                    disabled={!selectedUnassignedOrders.length}
                    onClick={assignSelected}
                    className={styles.panel__move}
                  >
                    Move to Router
                  </ButtonWithIcon>
                </div>
              </div>
            )}
          </div>

          <div className={styles.rightSide}>
            <div className={styles.rightHeader}>
              <WorkerSelect
                onSelectWorker={onChangeWorker}
                teams={teams?.results || []}
                workers={workers?.results || []}
                route={route as any}
                selectedWorker={selectedWorker!}
              />
            </div>
            <div className={styles.rightPanel}>
              {!!selectedWorker && (
                <WorkerAssignmentBox
                  invalidOrderIds={invalidOrderIds}
                  selectedWorker={selectedWorker!}
                  orders={route.deliveries}
                  teams={teams.results || []}
                  showEstTime={!shouldRecalculateForEstimatedTime}
                  onOrdersClick={handleNoteClick}
                  route={route as any}
                  rightItems={
                    route?.deliveries?.length
                      ? [
                          <div style={{minWidth: 260}}>
                            <div className={styles.calendarRoot}>
                              <DateTimePicker
                                placeholder="Start Time"
                                minTime={roundedTime ?? roundedTime}
                                maxTime={
                                  roundedTime
                                    ? new Date(new Date().setHours(23, 59, 59))
                                    : undefined
                                }
                                minDate={new Date()}
                                value={
                                  route.start_time
                                    ? parseJSON(route.start_time)
                                    : undefined
                                }
                                onChange={(data) => {
                                  onChangeStartTime(data);
                                }}
                              />
                            </div>
                          </div>,
                          <div className={styles.locationRoot}>
                            <div className={styles.icon_wrapper}>
                              <LocationIcon />
                            </div>
                            <GooglePlaceAutoComplete
                              wrapperClass={styles.googlePlaceAutoComplete}
                              style={{paddingLeft: 22}}
                              defaultValue={`${route.start_location || ''}`}
                              placeholder="Start Location"
                              onPlaceSelected={(placeDetail) => {
                                onChangeRouteParams({
                                  name: placeDetail.fullAddress,
                                  lat: placeDetail.lat,
                                  lng: placeDetail.lon,
                                  type: 'start',
                                });
                              }}
                              options={{
                                types: ['address'],
                              }}
                            />
                          </div>,

                          <div className={styles.locationRoot}>
                            <div className={styles.icon_wrapper}>
                              <LocationIcon />
                            </div>
                            <GooglePlaceAutoComplete
                              wrapperClass={styles.googlePlaceAutoComplete}
                              style={{paddingLeft: 22}}
                              placeholder="End Location"
                              defaultValue={`${route.end_location || ''}`}
                              onPlaceSelected={(placeDetail) => {
                                onChangeRouteParams({
                                  name: placeDetail.fullAddress,
                                  lat: placeDetail.lat,
                                  lng: placeDetail.lon,
                                  type: 'end',
                                });
                              }}
                              options={{
                                types: ['address'],
                              }}
                            />
                          </div>,
                        ]
                      : []
                  }
                />
              )}
            </div>

            {Boolean(search) && (
              <Resizable
                axis="y"
                className={styles.resizable}
                width={mapSize.width}
                height={mapSize.height}
                onResize={(_, {size}) => {
                  setMapSize({
                    height: Math.min(
                      Math.max(size.height, 99),
                      window.innerHeight - 99
                    ),
                    width: size.width,
                  });
                }}
                handle={(_, ref) => {
                  return <div ref={ref} className={styles.resizeHandle}></div>;
                }}
                resizeHandles={['n']}
              >
                <div
                  className={styles.mapView}
                  style={{
                    width: mapSize.width + 'px',
                    height: mapSize.height + 'px',
                    position: 'relative',
                    transition: isAnimating ? 'all 0.4s' : 'none',
                  }}
                >
                  <div className={classNames(styles.resizableHeader)}>
                    <div className={styles.resetButtonContainer}>
                      {!!search && (
                        <ButtonWithIcon
                          color="ghost"
                          size="md"
                          className={styles.reset}
                          icon={<RotateIcon width={16} height={16} />}
                          onClick={resetCalculate}
                        >
                          Reset
                        </ButtonWithIcon>
                      )}
                    </div>
                  </div>

                  <NewMap
                    orders={route.deliveries}
                    polylines={route?.polylines || []}
                    start_location={{
                      name: route.start_location,
                      lat: route.start_latitude,
                      lng: route.start_longitude,
                    }}
                    end_location={{
                      name: route.end_location,
                      lat: route.end_latitude,
                      lng: route.end_longitude,
                    }}
                    shouldDrawRoute
                    display={!!search}
                  />
                </div>
              </Resizable>
            )}
          </div>
        </DragDropContext>
        <div className={styles.bottomBar}>
          <div className={styles.historyButton}>
            <ButtonWithIcon
              icon={<HistoryIcon width={16} height={16} />}
              color="green"
              size="lg"
              component="a"
              to="/history/routes/page/1"
              className={styles.default_button}
            >
              History
            </ButtonWithIcon>
          </div>
          <ButtonWithIcon
            icon={<MagicIcon width={16} height={16} />}
            color="green"
            size="lg"
            onClick={calculateHandler}
            className={classNames(styles.ai_route, 'calculate-route')}
            disabled={
              !route.deliveries.length ||
              !isSubmittable ||
              calculateType === 'ai'
            }
          >
            {calculateType === 'ai' ? 'Calculating...' : 'Route with AI'}
          </ButtonWithIcon>
          <ButtonWithIcon
            icon={<MapIcon width={16} height={16} />}
            color="green"
            size="lg"
            disabled={
              !route.deliveries.length ||
              !isSubmittable ||
              calculateType === 'manual'
            }
            onClick={onMappingPressed}
            className={classNames(styles.default_button, 'mapping-route')}
          >
            {calculateType === 'manual' ? 'Calculating...' : 'Show On Map'}
          </ButtonWithIcon>
          {showSendButton && (
            <ButtonWithIcon
              icon={<SendIcon width={16} height={16} />}
              title={
                !!invalidOrderIds.length
                  ? `Please fix errors in the orders`
                  : undefined
              }
              color="green"
              size="lg"
              className={classNames(styles.default_button, 'send-route')}
              onClick={() => setIsSendRouteModalOpen(true)}
              disabled={!!invalidOrderIds.length}
            >
              Send {labels.deliveries}
            </ButtonWithIcon>
          )}
        </div>

        <OrderDetailsModal
          isOpen={noteModal}
          onClose={() => handleNoteModalClose()}
          order={activeOrder as Order}
          refetchOrders={() => {
            refetchOrders();
            refetch();
          }}
        />
        <SendRouteModal
          isIndividual={isIndividual}
          selectedWorker={selectedWorker}
          team={currentTeam}
          teams={teams?.results || []}
          workers={workers?.results || []}
          route={route as any}
          isOpen={isSendRouteModalOpen}
          onClose={() => setIsSendRouteModalOpen(false)}
          onSubmit={onSendRoute as any}
          onChangeWorker={onChangeWorker}
        />
        <Modal
          isOpen={moveOrderModal}
          onClose={() => setMoveToOrder(false)}
          onRequestClose={() => setMoveToOrder(false)}
          className={styles.list_wrapper}
        >
          <h3 style={{textAlign: 'center', marginTop: 30, marginBottom: 50}}>
            Are you sure you want to remove{' '}
            {selectedUnassignedOrders.length > 1
              ? `${selectedUnassignedOrders.length} stops`
              : ''}{' '}
            from the Inbox?
          </h3>
          <div className={styles.actions}>
            <Button
              type="submit"
              color="danger"
              size="md"
              onClick={moveToOrders}
            >
              Remove
            </Button>
          </div>
        </Modal>

        <Modal
          onRequestClose={() => setShowFilterModal(false)}
          isOpen={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          className={styles.filter_wrapper}
        >
          <div className={styles.filter_wrapper}>
            <Typography variant="h2">Filter</Typography>
            <FormGroup columns={2}>
              <Select
                className="Location_Select"
                isSearchable={false}
                onChange={handleChangeLocation}
                options={[
                  {
                    label: 'Locations',
                    options: locationOptions as any,
                  },
                ]}
                value={filter.location}
                placeholder="Location"
                isClearable
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !filter.location.length ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  Control: ({children, ...props}) => (
                    <components.Control {...props}>
                      <img
                        className={'locationIcon'}
                        src={locationIcon}
                        alt="location_icon"
                      />
                      {children}
                    </components.Control>
                  ),
                  SingleValue: ({children, ...props}) => {
                    return (
                      <components.SingleValue {...props}>
                        {isArray(props.selectProps.value)
                          ? props.selectProps.value
                              .map((item: {label: string}) => item.label)
                              .join(', ')
                          : ''}
                      </components.SingleValue>
                    );
                  },
                  IndicatorSeparator: () => null,
                }}
                menuPosition="fixed"
              />
              <Select
                isSearchable={false}
                onChange={onChangePriority}
                options={[
                  {label: 'High', value: 'high'},
                  {label: 'Medium', value: 'medium'},
                  {label: 'Low', value: 'low'},
                ]}
                value={filter.priority || null}
                placeholder={
                  <span className="select-placeholder-text">Priority</span>
                }
                menuPosition="fixed"
                isClearable
                components={{
                  DropdownIndicator: (props) =>
                    !filter.priority ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  SingleValue: (props) => {
                    return (
                      <components.SingleValue
                        {...props}
                        className={styles.select_option}
                      >
                        {props.data.label}
                        <PriorityFlag priority={props.data.value as any} />
                      </components.SingleValue>
                    );
                  },
                  Option: (props) => {
                    return (
                      <components.Option
                        {...props}
                        className={styles.select_option}
                      >
                        {props.data.label}
                        <PriorityFlag priority={props.data.value as any} />
                      </components.Option>
                    );
                  },
                }}
              />
            </FormGroup>

            <FormGroup columns={2}>
              <DateTimePicker
                showTimeSelect={false}
                icon={calendar}
                dateFormat="MM/dd/yy E"
                placeholder="Preference Date"
                customInput="customInput_Filter"
                value={filter.date ? parseJSON(filter.date) : undefined}
                minTime={filter.date ? parseJSON(filter.date) : undefined}
                onChange={onChangeData}
              />
              <Select
                isSearchable={false}
                onChange={onChangePreferenceTime}
                options={[
                  {label: 'Morning', value: 'morning'},
                  {label: 'Afternoon', value: 'afternoon'},
                  {label: 'Evening', value: 'evening'},
                ]}
                value={filter.time || null}
                placeholder={
                  <span className="select-placeholder-text">
                    Preference Time
                  </span>
                }
                isClearable
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !filter.time ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  Control: ({children, ...props}) => (
                    <components.Control {...props} className={styles.timeIcon}>
                      <img src={stat_time} alt="timer" />
                      {children}
                    </components.Control>
                  ),
                  IndicatorSeparator: () => null,
                  Option: (props) => {
                    const icons = {
                      morning: <Morning />,
                      afternoon: <Afternoon />,
                      evening: <Evening />,
                    };
                    return (
                      <components.Option
                        {...props}
                        className={styles.select_option}
                      >
                        {props.data.label}
                        {icons[props.data.value as keyof typeof icons]}
                      </components.Option>
                    );
                  },
                }}
              />
            </FormGroup>

            <div style={{gap: 8, display: 'flex', flexDirection: 'column'}}>
              <Typography variant="h2">Sort</Typography>
              <Select
                value={sortBy}
                onChange={(val: any) => {
                  setSortBy(val);
                }}
                isSearchable={false}
                options={sortOptions}
              />
            </div>
            <FormActionGroup className={styles.filter_actions}>
              <Button onClick={resetFilter} color="gray" size="md">
                Reset
              </Button>
              <Button onClick={applyFilter} color="blue" size="md">
                Apply
              </Button>
            </FormActionGroup>
          </div>
        </Modal>

        <PermissionModal getRef={(ref) => (permissionRef.current = ref)} />
      </div>
    </>
  );
}

export default DeliveryPlanner;
